import React from "react";
import {
  FlexGrid,
  Heading,
  Text,
  theme,
  CTA,
  formatCurrency,
} from "@clearabee/ui-library-native";
import { Button as SubmitButton } from "components/Core/Button/Button";
import { Platform, View } from "react-native";
import { Image } from "components/Image/Image";
import { styles, ids } from "../SkipBags.styles";
import placeholderImage from "lib/images/placeholder.png";
import { useTranslation } from "react-i18next";
import { useCatalogue } from "@clearabee/ui-sdk";
import { Input } from "components/Core/Input/Input";
import { useQuestionContext } from "lib/providers/questionProvider";
import { sortByPrice } from "lib/utils";
import { Form, useForm } from "lib/hooks/useForm";
import { z } from "zod";
import { FormState } from "../SkipBags.validation";

export const SelectBagCollectioOnlyBags = (): React.ReactElement => {
  const [translate] = useTranslation();
  const { defaultCatalogue } = useCatalogue();
  const context = useQuestionContext<FormState>();

  const form = useForm({
    defaultValues: context.get("products") ?? {},
    reValidateMode: "onChange",
    validationSchema: z
      .record(z.number().default(0))
      .refine((val) => Object.values(val).some((v) => !!v)),
    onSubmit: (values) => context.set("products", values).open("bagContents"),
  });

  const {
    formState: { isValid },
    trigger,
    setValue,
    submitForm,
  } = form;

  const filteredItems = sortByPrice(
    defaultCatalogue.items.filter(
      (item) => item.parentSku === "COLLECTIONONLYSKIPBAGS",
    ),
  );
  return (
    <Form state={form} useForm={false}>
      {({ Field }) => (
        <>
          <FlexGrid
            cellSpacing="xsmall"
            rowSpacing="xsmall"
            wrap="wrap"
            style={{ marginTop: theme.spacing.large }}
          >
            {filteredItems
              .filter((item) => !!item.price)
              .map(({ sku, title, img, price, description }) => (
                <FlexGrid.Cell
                  key={title}
                  base="50%"
                  medium="33.33%"
                  large="33.33%"
                >
                  <CTA
                    background="light"
                    orientation="portrait"
                    shadow={Platform.OS === "web"}
                    styles={styles.CTA}
                    dataSet={{ media: ids.CTA }}
                    contentStyles={{ flex: 1 }}
                    heading={
                      <Heading
                        level={4}
                        styles={styles.CTAHeading}
                        dataSet={{ media: ids.CTAHeading }}
                      >
                        {title}
                      </Heading>
                    }
                    textColor="brand"
                    pill="Collection Only"
                    image={
                      <Image
                        source={img ?? placeholderImage}
                        resizeMode="contain"
                        style={styles.CTAImage}
                      />
                    }
                    subheading={
                      <View
                        style={{
                          flex: 1,
                        }}
                      >
                        <Text
                          styles={styles.description}
                          dataSet={{ media: ids.description }}
                        >
                          {description}
                        </Text>
                        <Text
                          color="negative.base"
                          styles={styles.descriptionDisclaimer}
                          dataSet={{ media: ids.descriptionDisclaimer }}
                        >
                          {translate(
                            "hwa.screens.selectProduct.notSuitableSkipbagContents",
                          )}
                        </Text>
                        <View style={styles.subheadingContainer}>
                          <Text
                            styles={styles.subheading}
                            dataSet={{ media: ids.subheading }}
                            numberOfLines={1}
                          >
                            {formatCurrency(price ?? 0)}
                            <Text> {translate("common.incVat")}</Text>
                          </Text>
                        </View>
                      </View>
                    }
                  >
                    <View style={styles.quantityWrap}>
                      <Field name={sku}>
                        {({ field }) => (
                          <Input.Quantity
                            {...field}
                            min={0}
                            max={99}
                            onChange={(v) => {
                              setValue(sku, v);
                              trigger(sku);
                            }}
                          />
                        )}
                      </Field>
                    </View>
                  </CTA>
                </FlexGrid.Cell>
              ))}
          </FlexGrid>
          <SubmitButton
            styles={{ alignSelf: "center" }}
            size="medium"
            type="button"
            color="warning"
            onClick={submitForm}
            disabled={!isValid}
          >
            {translate("common.continue")}
          </SubmitButton>
        </>
      )}
    </Form>
  );
};
